
import { useState, useRef, useCallback } from "react";
import { useChatMessages } from "./useChatMessages";
import { useMessageHandling } from "./useMessageHandling";
import { useWaitingMessages } from "./useWaitingMessages";
import type { Message } from "@/types/chat";

export function useChatLogic() {
  const [isLoading, setIsLoading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  
  // Chat messages state
  const { 
    messages, 
    inputValue, 
    setInputValue,
    suggestionsVisible,
    setSuggestionsVisible,
    handleKeyDown: handleKeyDownBase,
    handleSuggestionClick,
    addMessage, 
    clearMessages 
  } = useChatMessages();
  
  // Message handling
  const { handleSendMessage: sendMessage } = useMessageHandling({
    addMessage,
    setIsLoading,
    scrollAreaRef
  });
  
  // Waiting messages
  const {
    waitingMessages,
    isShowingWaitingMessages,
    waitingMessageCategory,
    startWaitingMessages,
    stopWaitingMessages
  } = useWaitingMessages();

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = inputValue.trim();
    setInputValue("");
    setSuggestionsVisible(false);
    
    // Start waiting messages
    const stopWaiting = startWaitingMessages(userMessage);
    
    try {
      await sendMessage(userMessage);
    } finally {
      // Stop waiting messages
      stopWaitingMessages();
    }
  }, [inputValue, isLoading, sendMessage, startWaitingMessages, stopWaitingMessages, setInputValue, setSuggestionsVisible]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const wasEnter = handleKeyDownBase(e);
    if (wasEnter) {
      handleSendMessage();
    }
  }, [handleKeyDownBase, handleSendMessage]);

  const handleClearMessages = useCallback(() => {
    clearMessages();
    stopWaitingMessages();
    setSuggestionsVisible(true);
  }, [clearMessages, stopWaitingMessages, setSuggestionsVisible]);

  return {
    // Input state
    inputValue,
    setInputValue,
    suggestionsVisible,
    setSuggestionsVisible,
    handleSuggestionClick,
    
    // Loading state
    isLoading,
    
    // Messages
    messages,
    
    // Message handlers
    handleSendMessage,
    handleKeyDown,
    clearMessages: handleClearMessages,
    
    // UI refs
    scrollAreaRef,
    
    // Waiting messages
    waitingMessages,
    isShowingWaitingMessages,
    waitingMessageCategory
  };
}
